import { AnimationGroup } from '@babylonjs/core/Animations/animationGroup';
import { Scene } from '@babylonjs/core/scene';

function moveTowards(current: number, target: number, rate: number): number {
  if (target > current) {
    return Math.min(target, current + rate);
  } else {
    return Math.max(target, current - rate);
  }
}

type AnimState = 'idle' | 'walk' | 'run' | 'jump' | 'emote';

class AnimationController {
  game: any;
  scene: Scene | null;
  animations: Map<string, AnimationGroup>;
  targetState: AnimState;
  blendSpeed: number;
  nonIdleAnimations: AnimationGroup[];
  idleAnim: AnimationGroup | null;
  _currentEmote: AnimationGroup | null;
  _emoteTimeout: ReturnType<typeof setTimeout> | null;

  constructor(game: any) {
    this.game = game;
    this.scene = null;
    this.animations = new Map();
    this.targetState = 'idle';
    this.blendSpeed = 4.0;
    this.nonIdleAnimations = [];
    this.idleAnim = null;
    this._currentEmote = null;
    this._emoteTimeout = null;
  }

  init(scene: Scene) {
    this.scene = scene;
    this._discoverAnimations();
  }

  _discoverAnimations() {
    if (!this.scene) return;

    const mapping: { [key: string]: AnimState } = {
      'Idle': 'idle',
      'Walking': 'walk',
      'Running': 'run',
      'SambaDancing': 'emote',
      'Jump': 'jump',
    };

    for (const group of this.scene.animationGroups) {
      for (const [name, state] of Object.entries(mapping)) {
        if (group.name === name) {
          this.animations.set(state, group);
          break;
        }
      }
    }

    this.idleAnim = this.animations.get('idle') || null;
    if (this.idleAnim) {
      this.idleAnim.weight = 1;
      this.idleAnim.play(true);
    }

    this.nonIdleAnimations = [];
    for (const [key, anim] of this.animations) {
      if (key !== 'idle') {
        this.nonIdleAnimations.push(anim);
      }
    }
  }

  setState(state: AnimState) {
    this.targetState = state;
  }

  playEmote(emoteAnim: AnimationGroup, duration: number = 3000) {
    if (this._emoteTimeout) {
      clearTimeout(this._emoteTimeout);
    }

    this._currentEmote = emoteAnim;
    emoteAnim.weight = 0;
    if (!emoteAnim.isPlaying) emoteAnim.play(true);

    this._emoteTimeout = setTimeout(() => {
      this._currentEmote = null;
    }, duration);
  }

  update(delta: number) {
    if (!this.scene || !this.idleAnim) return;

    const targetAnim = this._currentEmote || this.animations.get(this.targetState) || this.idleAnim;

    let weightSum = 0;
    for (const animation of this.nonIdleAnimations) {
      if (animation === targetAnim) {
        animation.weight = moveTowards(animation.weight, 1, this.blendSpeed * delta);
      } else {
        animation.weight = moveTowards(animation.weight, 0, this.blendSpeed * delta);
      }
      if (animation.weight > 0 && !animation.isPlaying) animation.play(true);
      if (animation.weight === 0 && animation.isPlaying) animation.pause();

      weightSum += animation.weight;
    }

    this.idleAnim.weight = moveTowards(
      this.idleAnim.weight,
      Math.min(Math.max(1 - weightSum, 0.0), 1.0),
      this.blendSpeed * delta
    );

    if (this.idleAnim.weight > 0 && !this.idleAnim.isPlaying) {
      this.idleAnim.play(true);
    }
  }

  dispose() {
    if (this._emoteTimeout) {
      clearTimeout(this._emoteTimeout);
    }
    this.animations.forEach((anim) => {
      if (anim.isPlaying) anim.pause();
    });
    this.animations.clear();
    this.nonIdleAnimations = [];
    this.idleAnim = null;
  }
}

export { AnimationController };
export type { AnimState };
